import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useSettingsStore } from '../stores/useSettingsStore.js';

interface SidebarContextType {
  isOpen: boolean;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const sidebarCollapsed = useSettingsStore((state) => state.workspacePrefs.sidebarCollapsed);
  const setWorkspacePrefs = useSettingsStore((state) => state.setWorkspacePrefs);
  
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarOpen');
      if (saved !== null) {
        const legacyValue = JSON.parse(saved);
        localStorage.removeItem('sidebarOpen');
        return legacyValue;
      }
      return !sidebarCollapsed;
    }
    return !sidebarCollapsed;
  });

  const toggle = useCallback(() => {
    setIsOpen((prev: boolean) => {
      const newIsOpen = !prev;
      setWorkspacePrefs({ sidebarCollapsed: !newIsOpen });
      return newIsOpen;
    });
  }, [setWorkspacePrefs]);

  useEffect(() => {
    setIsOpen(!sidebarCollapsed);
  }, [sidebarCollapsed]);



  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    
    return () => {
      window.removeEventListener('keydown', handleKeyboard);
    };
  }, [toggle]);

  return (
    <SidebarContext.Provider value={{ isOpen, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
